import { type Project, useStores } from '@littlebird/sdk';
import {
  EMPTY_PROJECT_CAPABILITIES,
  formatRelativeDate,
} from '@littlebird/ui-utils';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import {
  type ComponentProps,
  type ComponentRef,
  type KeyboardEvent,
  type MouseEvent,
  forwardRef,
  memo,
  useCallback,
  useRef,
  useState,
} from 'react';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { FileDropZone } from '@/components/blocks/FileDropZone';
import { Layout } from '@/components/blocks/Layout/layout';
import { SidebarAddRow } from '@/components/blocks/Layout/Sidebar/SidebarAddRow';
import { SidebarListItemFrame } from '@/components/blocks/Layout/Sidebar/SidebarListItemFrame';
import { SidebarSectionHeading } from '@/components/blocks/Layout/Sidebar/SidebarSectionContainer';
import { SECTION_GAP } from '@/components/blocks/Layout/Sidebar/style';
import { ChatModeDropdown } from '@/components/blocks/MainLayout/Header/Chat/ChatModeDropdown';
import { MainLayoutHeaderTunnel } from '@/components/blocks/MainLayout/main-layout-tunnel';
import {
  type BaseModalFCProps,
  type ModalFC,
  ModalsManager,
} from '@/components/modals/modalsManager';
import {
  Button,
  Header,
  IconButton,
  Skeleton,
  Text,
  XStack,
  YStack,
} from '@/components/ui';
import { Dialog } from '@/components/ui/dialog';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Empty } from '@/components/ui/EmptyState';
import {
  EntityIcon,
  EntityIconChip,
  IconPicker,
} from '@/components/ui/icon-picker';
import { Input } from '@/components/ui/Input';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { TabButton } from '@/components/ui/tab-button';
import { HeaderSearch } from '@/design-system-v2/header-search';
import { Page } from '@/design-system-v2/page';
import { pageHeaderTunnel } from '@/design-system-v2/page-header-tunnel';
import { RoundIconButton } from '@/design-system-v2/round-icon-button';
import { NavRailPageTitle } from '@/features/nav-rail/nav-rail-page-title';
import { isNewTabClick, useTabs } from '@/features/tabs';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

import { CreateProjectModal } from './create-project-modal';
import { useProjectActions } from './project-actions';
import { ProjectActivityTab } from './project-activity-tab';
import { ProjectFilesTab } from './project-files-tab';
import { ProjectHeader } from './project-header';
import { ProjectInstructionsTab } from './project-instructions-tab';
import { useProjectSharedContent } from './project-shared-content.query';
import { ProjectSidebarRow } from './project-sidebar-row';

export const ProjectPanelHeader = observer(function ProjectPanelHeader() {
  const isListPage = useRouterState({
    select: (state) => state.location.pathname === '/projects',
  });
  return (
    <Header
      left={
        <>
          <NavRailPageTitle />
          <MainLayoutHeaderTunnel.Out />
        </>
      }
      right={
        <XStack className="items-center gap-2">
          <pageHeaderTunnel.Out />
          {userStore.isInternalUser && !isListPage && <ChatModeDropdown />}
        </XStack>
      }
    />
  );
});

export function ProjectItemBreadcrumb({
  project,
  title,
}: {
  project: Project | undefined;
  title: string;
}) {
  return (
    <MainLayoutHeaderTunnel.In>
      <XStack className="items-center gap-1 min-w-0">
        {project && (
          <>
            <ProjectBackLink project={project} />
            <Icons.chevronRight className="size-3 shrink-0 text-foreground-muted" />
          </>
        )}
        <span className="truncate text-sm text-foreground">{title}</span>
      </XStack>
    </MainLayoutHeaderTunnel.In>
  );
}

export function ProjectBackLink({ project }: { project: Project }) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      className={cn(
        'flex items-center gap-1.5 px-1.5 py-1 rounded-md text-sm',
        'text-foreground-muted hover:text-foreground hover:bg-surface-hover transition-colors'
      )}
      onClick={() => navigate({ to: `/projects/${project.id}` })}
    >
      <EntityIcon icon={project.icon} />
      <span className="truncate max-w-[120px]">{project.name}</span>
    </button>
  );
}

const formatProjectCount = (count: number, label: string) =>
  `${count} ${label}${count === 1 ? '' : 's'}`;

const formatProjectMeta = (chatCount: number, noteCount: number) => {
  const counts = [
    { count: chatCount, label: 'chat' },
    { count: noteCount, label: 'note' },
  ];
  const visibleCounts = counts
    .filter(({ count }) => count > 0)
    .map(({ count, label }) => formatProjectCount(count, label));

  return visibleCounts.length > 0
    ? visibleCounts.join(', ')
    : 'No chats or notes';
};

export const ProjectsOverview = memo(function ProjectsOverview() {
  const [query, setQuery] = useState('');

  return (
    <ScrollMask className="h-full scrollbar-hide">
      <Page.HeaderActions>
        <XStack className="items-center gap-3">
          <HeaderSearch
            value={query}
            onChange={setQuery}
            placeholder="Search projects..."
            aria-label="Search projects"
          />
          <NewProjectButton />
        </XStack>
      </Page.HeaderActions>
      <Page.Column top className="pb-6">
        <ProjectsContent query={query} />
      </Page.Column>
    </ScrollMask>
  );
});

const NewProjectButton = observer(function NewProjectButton() {
  return (
    <Page.HeaderButton
      id="new-project-button"
      icon={Icons.folderPlus}
      onClick={() => ModalsManager.show(CreateProjectModal)}
    >
      New project
    </Page.HeaderButton>
  );
});

const ProjectsContent = observer(function ProjectsContent({
  query,
}: {
  query: string;
}) {
  const { projects } = useStores();
  const projectList = [...projects.pinnedProjects, ...projects.recentProjects];
  const trimmedQuery = query.trim();
  const normalizedQuery = trimmedQuery.toLowerCase();
  const filteredProjects = normalizedQuery
    ? projectList.filter((project) =>
        project.name.toLowerCase().includes(normalizedQuery)
      )
    : projectList;
  const hasAnyProjects = projectList.length > 0;
  const isLoading = !projects.isLoaded;
  const isSearchActive = trimmedQuery !== '';

  if (isLoading) return <ProjectsListLoading />;

  if (filteredProjects.length === 0 && isSearchActive && hasAnyProjects) {
    return (
      <Empty.Root>
        <Empty.Media variant="icon">
          <Icons.search />
        </Empty.Media>
        <Empty.Header>
          <Empty.Title>No projects found</Empty.Title>
          <Empty.Description>Try adjusting your search.</Empty.Description>
        </Empty.Header>
      </Empty.Root>
    );
  }

  if (filteredProjects.length === 0) {
    return <ProjectsEmpty />;
  }

  return <ProjectsList projects={filteredProjects} />;
});

const ProjectsListLoading = () => {
  return (
    <YStack className="gap-1 pt-2">
      {[...Array(6)].map((_, index) => (
        <Skeleton key={index} className="h-14 rounded-2xl" />
      ))}
    </YStack>
  );
};

const ProjectsList = ({ projects }: { projects: Project[] }) => {
  return (
    <YStack className="gap-1 pt-2 no-record">
      {projects.map((project) => (
        <ProjectCard key={project.id} project={project} />
      ))}
    </YStack>
  );
};

const ProjectCard = observer(({ project }: { project: Project }) => {
  const { projects } = useStores();
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const { deleteProject, leaveProject, setProjectIcon } = useProjectActions();
  const [menuOpen, setMenuOpen] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const counts = projects.getItemCounts(project.id);
  const projectMeta = formatProjectMeta(counts.threads, counts.meetings);
  const lastUpdated = project.updatedAt ?? project.createdAt;

  const openProject = (event?: MouseEvent<HTMLDivElement>) => {
    if (menuOpen || iconPickerOpen) return;
    if (event && isNewTabClick(event)) {
      openTab({ path: `/projects/${project.id}` });
      return;
    }

    navigate({
      to: '/projects/$projectId',
      params: { projectId: project.id },
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openProject();
    }
  };

  const handleFavorite = () => {
    void projects.updateProject(project, { starred: !project.starred });
    trackEvent('PROJECT_STARRED', {
      favorite: !project.starred,
      origin: 'project-card',
    });
  };

  const handleRename = () => {
    ModalsManager.show(RenameProjectModal, {
      projectId: project.id,
      currentName: project.name,
    });
  };

  const handleDelete = async () => {
    await deleteProject(project.id);
  };

  const handleLeave = async () => {
    await leaveProject(project.id);
  };

  return (
    <div className="group relative">
      <XStack
        onClick={openProject}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        className={cn(
          'min-w-0 gap-4 items-center',
          'p-2 rounded-2xl',
          'cursor-default text-left',
          'focus-ring-inset',
          'group-hover:bg-surface-hover transition-colors',
          menuOpen && 'bg-surface-hover'
        )}
      >
        <XStack className="min-w-0 flex-1 items-center gap-4">
          <IconPicker
            value={project.icon}
            onChange={(projectIcon) =>
              void setProjectIcon(project, projectIcon)
            }
            onOpenChange={setIconPickerOpen}
          >
            <button
              type="button"
              aria-label="Change project icon"
              className={cn(
                'shrink-0 rounded-xl transition-opacity',
                'hover:opacity-80 focus-ring'
              )}
              onClick={(event) => event.stopPropagation()}
            >
              <EntityIconChip
                subtle
                icon={project.icon}
                size={20}
                className="size-12 rounded-xl"
              />
            </button>
          </IconPicker>
          <YStack className="flex-1 min-w-0 gap-0.5">
            <XStack className="items-center gap-1.5 min-w-0">
              <Text className="font-medium text-base truncate">
                {project.name}
              </Text>
              {project.starred && (
                <Icons.starFilled className="size-3.5 text-foreground-muted shrink-0" />
              )}
            </XStack>
            <Text className="text-xs text-foreground-muted truncate">
              {projectMeta}
            </Text>
          </YStack>
        </XStack>

        <XStack className="shrink-0 items-center relative min-w-0 justify-end">
          {lastUpdated && (
            <Text
              className={cn(
                'text-xs text-foreground-muted px-2 w-20 text-right pointer-events-none',
                'transition-opacity duration-150',
                menuOpen && 'opacity-0',
                'group-hover:opacity-0 group-has-[:focus-visible]:opacity-0'
              )}
            >
              {formatRelativeDate(lastUpdated)}
            </Text>
          )}
          <XStack className="absolute right-0 top-1/2 -translate-y-1/2 items-center">
            <DropdownMenu.Root open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenu.Trigger asChild>
                <IconButton
                  icon={Icons.moreHorizontal}
                  size="default"
                  variant="secondary"
                  className={cn(
                    'opacity-0 transition-opacity duration-150',
                    'data-[state=open]:opacity-100 data-[state=open]:bg-secondary-hover',
                    'touch:opacity-100',
                    'group-hover:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100'
                  )}
                  onClick={(e) => e.stopPropagation()}
                />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content
                align="end"
                className="min-w-44 max-w-64"
                fit
              >
                <ProjectCardDropdownItems
                  project={project}
                  onFavorite={handleFavorite}
                  onRename={() => {
                    setMenuOpen(false);
                    handleRename();
                  }}
                  onDelete={handleDelete}
                  onLeave={handleLeave}
                />
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </XStack>
        </XStack>
      </XStack>
    </div>
  );
});
export function ProjectCardDropdownItems({
  project,
  onFavorite,
  onRename,
  onDelete,
  onLeave,
}: {
  project: Project;
  onFavorite: () => void;
  onRename: () => void;
  onDelete: () => void;
  onLeave: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const { canRename, canDelete, canLeave } =
    project?.capabilities ?? EMPTY_PROJECT_CAPABILITIES;

  return (
    <>
      <DropdownMenu.Item
        onClick={(e) => {
          e.stopPropagation();
          onFavorite();
        }}
      >
        {project.starred ? (
          <Icons.starFilled className="text-yellow-500" />
        ) : (
          <Icons.star />
        )}
        {project.starred ? 'Unfavorite' : 'Favorite'}
      </DropdownMenu.Item>
      {canRename && (
        <DropdownMenu.Item
          onClick={(e) => {
            e.stopPropagation();
            onRename();
          }}
        >
          <Icons.edit />
          Rename
        </DropdownMenu.Item>
      )}
      {canLeave && (
        <>
          <DropdownMenu.Separator />
          <DropdownMenu.Item
            variant="destructive"
            onClick={(e) => {
              e.stopPropagation();
              if (!confirmLeave) {
                e.preventDefault();
                setConfirmLeave(true);
                return;
              }
              onLeave();
            }}
          >
            {confirmLeave ? <Icons.check /> : <Icons.logOut />}
            {confirmLeave ? 'Leave project?' : 'Leave project'}
          </DropdownMenu.Item>
        </>
      )}
      {canDelete && (
        <>
          <DropdownMenu.Separator />
          <DropdownMenu.Item
            variant="destructive"
            onClick={(e) => {
              e.stopPropagation();
              if (!confirmDelete) {
                e.preventDefault();
                setConfirmDelete(true);
                return;
              }
              onDelete();
            }}
          >
            {confirmDelete ? <Icons.check /> : <Icons.trash />}
            {confirmDelete ? 'Confirm?' : 'Delete'}
          </DropdownMenu.Item>
        </>
      )}
    </>
  );
}

const ProjectsEmpty = () => {
  return (
    <Empty.Root variant="action">
      <Empty.Header>
        <Empty.Title>Create your first project</Empty.Title>
        <Empty.Description>
          Keep related chats and meeting notes together in one place.
        </Empty.Description>
      </Empty.Header>
      <Empty.Actions>
        <Button onClick={() => ModalsManager.show(CreateProjectModal)}>
          Create project
        </Button>
      </Empty.Actions>
    </Empty.Root>
  );
};

interface RenameProjectModalProps extends BaseModalFCProps {
  projectId: string;
  currentName: string;
}

const RenameProjectModal: ModalFC<RenameProjectModalProps> = ({
  projectId,
  currentName,
  open,
  onOpenChange,
}) => {
  const { renameProject } = useProjectActions();
  const [name, setName] = useState(currentName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || trimmedName === currentName || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      await renameProject(projectId, trimmedName);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setName(currentName);
        }
        onOpenChange(nextOpen);
      }}
    >
      <Dialog.Content
        showCloseButton
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
          inputRef.current?.select();
        }}
      >
        <YStack className="gap-5">
          <Dialog.Title className="text-lg font-medium text-foreground">
            Rename project
          </Dialog.Title>

          <Input
            ref={inputRef}
            type="text"
            label="Project name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Project name"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleSubmit();
              }
            }}
          />

          <XStack className="justify-end gap-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={() => void handleSubmit()}
              disabled={
                !name.trim() || name.trim() === currentName || isSubmitting
              }
            >
              Rename
            </Button>
          </XStack>
        </YStack>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export const ProjectView = observer(function ProjectView({
  projectId,
}: {
  projectId: string;
}) {
  const { projects } = useStores();
  const project = projects.getById(projectId);
  const chatsQuery = useProjectSharedContent('chat', projectId);
  const meetingsQuery = useProjectSharedContent('meeting', projectId);
  const currentUserId = userStore.userId ? Number(userStore.userId) : null;
  const [activeTab, setActiveTab] = useState<
    'activity' | 'files' | 'instructions'
  >('activity');
  const filesTabOpen = activeTab === 'files';

  if (!project) {
    return (
      <YStack className="flex-1 items-center justify-center text-foreground-muted">
        Project not found
      </YStack>
    );
  }

  return (
    <div className="h-full relative">
      {!filesTabOpen && <FileDropZone show={uiStore.isDraggingFile} />}
      <Layout.Root
        className={cn(
          'px-0',
          Page.topPadding,
          !filesTabOpen && uiStore.isDraggingFile && 'opacity-10'
        )}
      >
        <Layout.Content className={cn(Page.column(), filesTabOpen && 'flex-1')}>
          <YStack className={cn('gap-6', filesTabOpen && 'flex-1')}>
            <YStack className="relative">
              {filesTabOpen && (
                <FileDropZone
                  show={uiStore.isDraggingFile}
                  message="Drop files here to upload to chat"
                />
              )}
              <YStack
                className={cn(
                  'gap-6',
                  filesTabOpen && uiStore.isDraggingFile && 'opacity-10'
                )}
              >
                <ProjectHeader project={project} />
              </YStack>
            </YStack>

            <XStack className="gap-4 border-b border-border">
              <TabButton
                label="Chats and notes"
                active={activeTab === 'activity'}
                count={
                  chatsQuery.isLoading || meetingsQuery.isLoading
                    ? undefined
                    : chatsQuery.data.length + meetingsQuery.data.length
                }
                onClick={() => setActiveTab('activity')}
              />
              <TabButton
                label="Customize"
                active={activeTab === 'instructions'}
                onClick={() => {
                  setActiveTab('instructions');
                  trackEvent('PROJECT_INSTRUCTIONS_TAB_CLICKED');
                }}
              />
              <TabButton
                label="Files"
                active={activeTab === 'files'}
                onClick={() => {
                  setActiveTab('files');
                  trackEvent('PROJECT_FILES_TAB_CLICKED');
                }}
              />
            </XStack>

            <YStack className={cn('gap-3', filesTabOpen && 'flex-1')}>
              {activeTab === 'activity' && (
                <ProjectActivityTab
                  projectId={project.id}
                  currentUserId={currentUserId ?? -1}
                />
              )}
              {activeTab === 'instructions' && (
                <ProjectInstructionsTab project={project} />
              )}
              {activeTab === 'files' && (
                <ProjectFilesTab
                  projectId={project.id}
                  canEditContent={project.capabilities.canEditContent}
                />
              )}
            </YStack>
          </YStack>
        </Layout.Content>
      </Layout.Root>
    </div>
  );
});

export type ProjectSidebarListItem = {
  id: string;
} & (
  | { type: 'projects-header' }
  | { type: 'project' }
  | { type: 'add-project' }
  | { type: 'more-projects' }
);

const ProjectsPlusIcon = forwardRef<
  ComponentRef<LBIcon>,
  ComponentProps<LBIcon>
>((props, ref) => (
  <svg ref={ref} viewBox="0 0 16 16" fill="currentColor" {...props}>
    <path d="M8 2.74a.66.66 0 0 1 .66.66v3.94h3.94a.66.66 0 0 1 0 1.32H8.66v3.94a.66.66 0 0 1-1.32 0V8.66H3.4a.66.66 0 0 1 0-1.32h3.94V3.4A.66.66 0 0 1 8 2.74" />
  </svg>
));

export const ProjectSidebarHeaderRow = observer(
  function ProjectSidebarHeaderRow({
    hasProjects,
    isExpanded,
    onExpandedChange,
    onSelect,
  }: {
    hasProjects: boolean;
    isExpanded: boolean;
    onExpandedChange: (isExpanded: boolean) => void;
    onSelect: React.MouseEventHandler<HTMLButtonElement>;
  }) {
    return (
      <SidebarSectionHeading
        isExpanded={isExpanded}
        onExpandedChange={onExpandedChange}
        onNavigate={onSelect}
        rightSlot={
          <>
            <RoundIconButton
              label="Open all projects"
              icon={Icons.arrowUpRight}
              size="xs"
              className="-my-0.5"
              onClick={onSelect}
            />
            {hasProjects && (
              <RoundIconButton
                label="New project"
                icon={ProjectsPlusIcon}
                size="xs"
                className="-my-0.5"
                onClick={() => ModalsManager.show(CreateProjectModal)}
              />
            )}
          </>
        }
      >
        Projects
      </SidebarSectionHeading>
    );
  }
);

export function ProjectSidebarMoreRow({
  isActive,
  onSelect,
}: {
  isActive: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm w-full transition-colors',
        isActive
          ? 'bg-surface-active text-foreground'
          : 'text-foreground-muted hover:text-foreground hover:bg-surface-hover'
      )}
      onClick={onSelect}
    >
      <Icons.moreHorizontal className="size-3.5 shrink-0" />
      <span>More</span>
    </button>
  );
}

export function isProjectSidebarListItem(item: {
  type: string;
}): item is ProjectSidebarListItem {
  return (
    item.type === 'projects-header' ||
    item.type === 'add-project' ||
    item.type === 'more-projects' ||
    item.type === 'project'
  );
}

export function ProjectSidebarVListItem({
  item,
  section = 'projects',
  isSectionBoundary,
  hasFollowingSection = false,
  isProjectsPinned,
  isProjectsExpanded,
  onProjectsExpandedChange,
  isExpanded,
  onToggleExpanded,
}: {
  item: ProjectSidebarListItem;
  section?: 'projects' | 'favorites';
  isSectionBoundary: boolean;
  hasFollowingSection?: boolean;
  isProjectsPinned: boolean;
  isProjectsExpanded: boolean;
  onProjectsExpandedChange: (isExpanded: boolean) => void;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}) {
  const { projects } = useStores();
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const pathname = useRouterState().location.pathname;
  const { deleteProject, leaveProject } = useProjectActions();
  const selectProject = useCallback(
    (projectId: string, e?: MouseEvent) => {
      if (e && isNewTabClick(e)) {
        openTab({ path: `/projects/${projectId}` });
      } else {
        navigate({ to: `/projects/${projectId}` });
      }
    },
    [openTab, navigate]
  );

  const handleDeleteProject = useCallback(
    async (projectId: string, isActive: boolean) => {
      const didDelete = await deleteProject(projectId);

      if (didDelete && isActive) {
        navigate({ to: '/chats' });
      }
    },
    [deleteProject, navigate]
  );

  const handleLeaveProject = useCallback(
    async (projectId: string, isActive: boolean) => {
      const didLeave = await leaveProject(projectId);

      if (didLeave && isActive) {
        navigate({ to: '/chats' });
      }
    },
    [leaveProject, navigate]
  );

  const handleFavoriteProject = useCallback(
    (project: Project) => {
      void projects.updateProject(project, { starred: !project.starred });
      trackEvent('PROJECT_STARRED', {
        favorite: !project.starred,
        origin: 'sidebar',
      });
    },
    [projects]
  );

  if (item.type === 'projects-header') {
    return (
      <YStack
        aria-hidden={isProjectsPinned ? true : undefined}
        inert={isProjectsPinned ? true : undefined}
        data-sidebar-section-heading="projects"
        className={cn(
          'pb-0.5 no-record',
          hasFollowingSection && SECTION_GAP,
          isProjectsPinned && 'invisible'
        )}
        data-sidebar-section-boundary={
          isSectionBoundary ? 'projects' : undefined
        }
      >
        <ProjectSidebarHeaderRow
          hasProjects={projects.orderedData.length > 0}
          isExpanded={isProjectsExpanded}
          onExpandedChange={onProjectsExpandedChange}
          onSelect={(event) => {
            if (isNewTabClick(event)) {
              openTab({ path: '/projects' });
            } else {
              navigate({ to: '/projects' });
            }
          }}
        />
      </YStack>
    );
  }

  if (item.type === 'add-project') {
    return (
      <SidebarListItemFrame
        section="projects"
        isSectionBoundary={isSectionBoundary}
        hasFollowingSection={hasFollowingSection}
      >
        <SidebarAddRow
          icon={Icons.folderPlus}
          label="Add Project"
          onClick={() => ModalsManager.show(CreateProjectModal)}
        />
      </SidebarListItemFrame>
    );
  }

  if (item.type === 'more-projects') {
    return (
      <SidebarListItemFrame
        section="projects"
        isSectionBoundary={isSectionBoundary}
        hasFollowingSection={hasFollowingSection}
      >
        <ProjectSidebarMoreRow
          isActive={pathname === '/projects'}
          onSelect={() => navigate({ to: '/projects' })}
        />
      </SidebarListItemFrame>
    );
  }

  const project = projects.getById(item.id);
  if (!project) return null;
  const isActive = pathname === `/projects/${project.id}`;

  return (
    <SidebarListItemFrame
      section={section}
      isSectionBoundary={isSectionBoundary}
      hasFollowingSection={hasFollowingSection}
      data-testid={`project-${item.id}`}
    >
      <ProjectSidebarRow
        project={project}
        isActive={isActive}
        onSelect={(e) => selectProject(project.id, e)}
        onDelete={() => {
          void handleDeleteProject(project.id, isActive);
        }}
        onLeave={() => {
          void handleLeaveProject(project.id, isActive);
        }}
        onFavorite={() => handleFavoriteProject(project)}
        isExpanded={isExpanded}
        onToggleExpanded={onToggleExpanded}
      />
    </SidebarListItemFrame>
  );
}
